import React, { useEffect, useState, useCallback } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Coins, TrendingUp, Clock, Gift, ListChecks, History, Sparkles, Link2, Info, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const nf = (n) => (Number(n) || 0).toLocaleString("id-ID");

const EVENT_LABEL = {
  welcome: "Bonus profil lengkap", connect_channel: "Connect channel", content_base: "Upload sesuai brief",
  content_pass: "Lolos approval", content_ontime: "Tepat waktu", content_aff: "Konten affiliate approved",
  order_valid: "Order valid", gmv: "Bonus GMV", views: "Bonus views", cr: "Bonus conversion",
  event_valid: "Event tervalidasi", campaign_done: "Campaign selesai", review_new: "Review produk baru",
  referral_active: "Referral aktif", live_eligible: "Live eligible", consistency: "Konsistensi penjualan",
  mission: "Bonus misi bulanan", redeem: "Penukaran reward", redeem_refund: "Refund penukaran",
  adjust: "Penyesuaian admin", penalty: "Penalti", reversal: "Reversal",
};

const Card = ({ icon: Icon, label, value, sub, tone }) => (
  <div className="rounded-2xl border border-border bg-white p-5" data-testid={`points-card-${label}`}>
    <Icon className={`w-5 h-5 ${tone}`} />
    <p className="mt-3 font-display text-3xl text-showty-navy">{value}</p>
    <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
    {sub && <p className="text-[11px] text-showty-gold mt-1">{sub}</p>}
  </div>
);

const PathSummary = ({ s, label }) => {
  if (!s) return null;
  const pct = s.next_tier ? Math.min(100, Math.round(((s.tier_points - s.tier.min) / (s.next_tier.min - s.tier.min)) * 100)) : 100;
  return (
    <div className="rounded-2xl border border-border bg-white p-5" data-testid={`path-summary-${s.path}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
        <span className="text-[11px] rounded-full bg-showty-pink/40 px-2.5 py-0.5 text-showty-navy font-semibold">{s.tier.name}</span>
      </div>
      <p className="mt-2 font-display text-2xl text-showty-navy">{nf(s.tier_points)} <span className="text-sm font-sans text-muted-foreground">tier point</span></p>
      <div className="mt-3 h-2.5 rounded-full bg-showty-cream overflow-hidden">
        <div className="h-full bg-showty-red transition-all" style={{ width: `${pct}%` }} />
      </div>
      {s.next_tier ? (
        <p className="text-xs text-muted-foreground mt-1.5">Lagi <b className="text-showty-navy">{nf(s.to_next)}</b> point menuju <b>{s.next_tier.name}</b></p>
      ) : <p className="text-xs text-showty-gold mt-1.5">Tier tertinggi tercapai! ✨</p>}
    </div>
  );
};

export default function PointsPanel() {
  const { user } = useAuth();
  const [sum, setSum] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [missions, setMissions] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [reds, setReds] = useState([]);
  const [rules, setRules] = useState(null);
  const [channels, setChannels] = useState([]);
  const [ch, setCh] = useState({ platform: "tiktok", handle: "" });

  const load = useCallback(async () => {
    const [s, l, m, rw, rd, ru, cn] = await Promise.all([
      api.get("/points/summary"), api.get("/points/ledger"), api.get("/points/missions"),
      api.get("/points/rewards"), api.get("/points/redemptions"), api.get("/points/rules"),
      api.get("/points/channels"),
    ]);
    setSum(s.data); setLedger(l.data); setMissions(m.data); setRewards(rw.data);
    setReds(rd.data); setRules(ru.data); setChannels(cn.data);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const primary = sum?.primary || "kol";
  const primarySum = sum ? sum[primary] : null;
  const otherSum = sum ? sum[primary === "kol" ? "aff" : "kol"] : null;
  const hasOther = otherSum && (otherSum.tier_points > 0 || otherSum.reward_available > 0);

  const redeem = async (r) => {
    try {
      await api.post(`/points/rewards/${r.id}/redeem`);
      toast.success(`Berhasil menukar ${r.name}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menukar reward"); }
  };

  const addChannel = async () => {
    if (!ch.handle.trim()) return toast.error("Isi username/handle");
    try { await api.post("/points/channels", ch); toast.success("Channel ditambahkan, menunggu verifikasi admin"); setCh({ ...ch, handle: "" }); load(); }
    catch (e) { toast.error("Gagal menambah channel"); }
  };

  const rulesList = rules ? Object.entries(primary === "kol" ? rules.kol_rules : rules.aff_rules) : [];

  return (
    <div data-testid="points-panel">
      <div className="rounded-xl bg-showty-navy text-white px-4 py-3 mb-5 flex items-start gap-2" data-testid="points-disclaimer">
        <Info className="w-4 h-4 shrink-0 mt-0.5" />
        <p className="text-sm">Point bukan rupiah, tidak dapat dicairkan, dan terpisah dari komisi affiliate. <span className="opacity-80">Tier point dihitung dari point valid 180 hari terakhir; saldo reward berlaku 12 bulan dan bisa ditukar tanpa menurunkan tier.</span></p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <Card icon={Coins} label="Saldo Reward (bisa ditukar)" value={nf(sum?.reward_pooled)} tone="text-showty-red" />
        <Card icon={Clock} label="Point Pending" value={nf(primarySum?.pending)} tone="text-showty-gold" />
        <Card icon={TrendingUp} label={`Tier Point (${primary === "kol" ? "KOL" : "Affiliator"})`} value={nf(primarySum?.tier_points)} sub={primarySum?.tier?.name} tone="text-showty-navy" />
        <Card icon={Sparkles} label="Misi Bulan Ini Selesai" value={`${missions?.missions?.filter((m) => m.status === "completed").length || 0}/${missions?.missions?.length || 0}`} tone="text-showty-red" />
      </div>

      <div className={`grid gap-4 mb-6 ${hasOther ? "sm:grid-cols-2" : ""}`}>
        <PathSummary s={primarySum} label={primary === "kol" ? "Jalur Beauty Creator (KOL)" : "Jalur Affiliator"} />
        {hasOther && <PathSummary s={otherSum} label={primary === "kol" ? "Jalur Affiliator" : "Jalur Beauty Creator (KOL)"} />}
      </div>

      <Tabs defaultValue="rewards">
        <TabsList className="bg-showty-secondary flex-wrap h-auto">
          <TabsTrigger value="rewards" data-testid="pt-tab-rewards"><Gift className="w-4 h-4 mr-1.5" /> Reward Store</TabsTrigger>
          <TabsTrigger value="missions" data-testid="pt-tab-missions"><ListChecks className="w-4 h-4 mr-1.5" /> Monthly Missions</TabsTrigger>
          <TabsTrigger value="history" data-testid="pt-tab-history"><History className="w-4 h-4 mr-1.5" /> Riwayat Point</TabsTrigger>
          <TabsTrigger value="redemptions" data-testid="pt-tab-redemptions"><Coins className="w-4 h-4 mr-1.5" /> Penukaran</TabsTrigger>
          <TabsTrigger value="earn" data-testid="pt-tab-earn"><Info className="w-4 h-4 mr-1.5" /> Cara Dapat Point</TabsTrigger>
          <TabsTrigger value="channels" data-testid="pt-tab-channels"><Link2 className="w-4 h-4 mr-1.5" /> Channel</TabsTrigger>
        </TabsList>

        {/* Reward store */}
        <TabsContent value="rewards" className="mt-5">
          {rewards.length === 0 ? <Empty text="Belum ada reward" /> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="reward-store">
              {rewards.map((r) => {
                const affordable = (sum?.reward_pooled || 0) >= r.cost && r.stock > 0;
                return (
                  <div key={r.id} className="rounded-2xl border border-border bg-white overflow-hidden flex flex-col" data-testid={`reward-${r.id}`}>
                    {r.image_url && <div className="h-32 overflow-hidden"><img src={r.image_url} alt={r.name} className="w-full h-full object-cover" /></div>}
                    <div className="p-4 flex flex-col flex-1">
                      <h4 className="font-display text-lg text-showty-navy">{r.name}</h4>
                      {r.description && <p className="text-xs text-muted-foreground mt-0.5 flex-1">{r.description}</p>}
                      {r.terms && <p className="text-[11px] text-muted-foreground italic mt-1">Syarat: {r.terms}</p>}
                      <div className="mt-3 flex items-center justify-between">
                        <span className="font-display text-xl text-showty-red">{nf(r.cost)} <span className="text-xs font-sans text-muted-foreground">pt</span></span>
                        <span className="text-[11px] text-muted-foreground">Stok: {r.stock}</span>
                      </div>
                      <Button disabled={!affordable} onClick={() => redeem(r)} className="mt-3 rounded-full bg-showty-red hover:bg-[#9e1f24] text-white disabled:opacity-40" data-testid={`redeem-${r.id}`}>
                        {r.stock <= 0 ? "Stok habis" : affordable ? "Tukar Sekarang" : "Point kurang"}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Missions */}
        <TabsContent value="missions" className="mt-5">
          <p className="text-sm text-muted-foreground mb-3">Misi periode {missions?.cohort}. Bonus diberikan sekali per misi per bulan, di atas point aktivitas dasar.</p>
          <div className="space-y-3" data-testid="missions-list">
            {missions?.missions?.map((m) => (
              <div key={m.id} className="rounded-xl border border-border bg-white p-4 flex items-center gap-4" data-testid={`mission-${m.id}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${m.status === "completed" ? "bg-green-100 text-green-600" : "bg-showty-cream text-showty-gold"}`}>
                  {m.status === "completed" ? <CheckCircle2 className="w-5 h-5" /> : <ListChecks className="w-5 h-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-showty-navy">{m.label}</p>
                  <div className="mt-1.5 h-2 rounded-full bg-showty-cream overflow-hidden max-w-xs">
                    <div className="h-full bg-showty-navy" style={{ width: `${Math.min(100, (m.progress / m.target) * 100)}%` }} />
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">Progress {m.progress}/{m.target}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-display text-lg text-showty-red">+{nf(m.bonus)}</p>
                  <span className={`text-[11px] font-semibold ${m.status === "completed" ? "text-green-600" : "text-muted-foreground"}`}>{m.status === "completed" ? "Selesai" : "Berjalan"}</span>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* History */}
        <TabsContent value="history" className="mt-5">
          {ledger.length === 0 ? <Empty text="Belum ada riwayat point" /> : (
            <div className="rounded-2xl border border-border bg-white overflow-hidden overflow-x-auto" data-testid="ledger-table">
              <table className="w-full text-sm">
                <thead className="bg-showty-navy text-white"><tr className="text-left">
                  <th className="px-3 py-2 text-xs font-medium">Aktivitas</th><th className="px-3 py-2 text-xs font-medium">Jalur</th>
                  <th className="px-3 py-2 text-xs font-medium">Point</th><th className="px-3 py-2 text-xs font-medium">Status</th>
                  <th className="px-3 py-2 text-xs font-medium">Kedaluwarsa</th>
                </tr></thead>
                <tbody>
                  {ledger.map((e) => (
                    <tr key={e.id} className="border-t border-border even:bg-showty-cream/40" data-testid={`ledger-row-${e.id}`}>
                      <td className="px-3 py-2 text-showty-navy">{EVENT_LABEL[e.event_type] || e.event_type}<span className="block text-[11px] text-muted-foreground">{e.reason}</span></td>
                      <td className="px-3 py-2 uppercase text-xs">{e.path}</td>
                      <td className={`px-3 py-2 font-semibold ${e.points >= 0 ? "text-green-600" : "text-showty-red"}`}>{e.points >= 0 ? "+" : ""}{nf(e.points)}</td>
                      <td className="px-3 py-2"><span className="text-[11px] rounded-full bg-showty-pink/30 px-2 py-0.5">{e.status}</span></td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{e.reward_expires_at ? new Date(e.reward_expires_at).toLocaleDateString("id-ID") : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* Redemptions */}
        <TabsContent value="redemptions" className="mt-5">
          {reds.length === 0 ? <Empty text="Belum ada penukaran" /> : (
            <div className="space-y-3" data-testid="redemptions-list">
              {reds.map((r) => (
                <div key={r.id} className="rounded-xl border border-border bg-white p-4 flex items-center justify-between gap-3" data-testid={`redemption-${r.id}`}>
                  <div><p className="font-medium text-showty-navy">{r.reward_name}</p><p className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString("id-ID")}</p></div>
                  <div className="text-right"><p className="text-showty-red font-semibold">-{nf(r.cost)} pt</p>
                    <span className={`text-[11px] rounded-full px-2 py-0.5 ${r.status === "fulfilled" ? "bg-green-100 text-green-700" : r.status === "cancelled" ? "bg-red-100 text-red-700" : "bg-showty-cream text-showty-gold"}`}>{r.status}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Earn rules */}
        <TabsContent value="earn" className="mt-5">
          <p className="text-sm text-muted-foreground mb-3">Cara mendapatkan point untuk jalur {primary === "kol" ? "Beauty Creator (KOL)" : "Affiliator"}:</p>
          <div className="grid sm:grid-cols-2 gap-2" data-testid="earn-rules">
            {rulesList.map(([k, v]) => (
              <div key={k} className="rounded-lg border border-border bg-white px-3 py-2 flex items-center justify-between text-sm">
                <span className="text-showty-navy">{EARN_LABEL[k] || k}</span>
                <span className="font-semibold text-showty-red">+{nf(v)}</span>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Channels */}
        <TabsContent value="channels" className="mt-5">
          <p className="text-sm text-muted-foreground mb-3">Hubungkan channel TikTok/Instagram kamu. Setelah diverifikasi admin, kamu dapat +point per platform (sekali).</p>
          <div className="flex flex-wrap items-end gap-2 mb-4">
            <div><label className="text-xs text-muted-foreground">Platform</label>
              <Select value={ch.platform} onValueChange={(v) => setCh({ ...ch, platform: v })}>
                <SelectTrigger className="mt-1 h-9 w-36" data-testid="channel-platform"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="tiktok">TikTok</SelectItem><SelectItem value="instagram">Instagram</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[180px]"><label className="text-xs text-muted-foreground">Username / handle</label>
              <Input className="mt-1 h-9" placeholder="@username" value={ch.handle} onChange={(e) => setCh({ ...ch, handle: e.target.value })} data-testid="channel-handle" /></div>
            <Button onClick={addChannel} className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white h-9" data-testid="channel-add">Tambah</Button>
          </div>
          <div className="space-y-2" data-testid="channels-list">
            {channels.map((c) => (
              <div key={c.id} className="rounded-lg border border-border bg-white px-3 py-2 flex items-center justify-between text-sm" data-testid={`channel-${c.id}`}>
                <span className="text-showty-navy capitalize">{c.platform} · {c.handle}</span>
                <span className={`text-[11px] rounded-full px-2 py-0.5 ${c.verified ? "bg-green-100 text-green-700" : "bg-showty-cream text-showty-gold"}`}>{c.verified ? "Terverifikasi" : "Menunggu verifikasi"}</span>
              </div>
            ))}
            {channels.length === 0 && <Empty text="Belum ada channel" />}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const EARN_LABEL = {
  welcome: "Profil lengkap / onboarding", connect_channel: "Connect channel terverifikasi (per platform)",
  upload_brief: "Upload sesuai brief (approved)", approval_pass: "Lolos approval", on_time: "Tepat waktu & approved",
  views_5k: "Views organik >5.000", views_20k: "Views organik >20.000", views_100k: "Views organik >100.000",
  order_valid: "Order affiliate valid (per order)", gmv_500k: "GMV net bulanan ≥Rp500rb", gmv_1m: "GMV net bulanan ≥Rp1jt",
  gmv_3m: "GMV net bulanan ≥Rp3jt", gmv_5m: "GMV net bulanan ≥Rp5jt", gmv_10m: "GMV net bulanan ≥Rp10jt", gmv_25m: "GMV net bulanan ≥Rp25jt",
  cr_2: "Conversion rate ≥2%", cr_3: "Conversion rate ≥3%", cr_5: "Conversion rate ≥5%",
  campaign_done: "Campaign khusus selesai", event_valid: "Event/community tervalidasi", review_new: "Review produk baru approved",
  referral_active: "Referral creator aktif", content_approved: "Konten affiliate approved", live_eligible: "Live eligible", consistency: "Konsistensi penjualan mingguan",
};

const Empty = ({ text }) => (
  <div className="rounded-2xl border border-dashed border-border bg-white/50 py-14 text-center text-sm text-muted-foreground" data-testid="empty-state">{text}</div>
);
